import { PixiApp } from "~/src/pixi-app";
import { Layer } from "@pixi/layers";
import {
  diffuseGroup,
  normalGroup,
  lightGroup,
  PointLight,
  AmbientLight,
} from "pixi-lights";
import { Sprite, Container } from "pixi.js";
import gsap from "gsap";
import { singleton } from "tsyringe";
import { EventBus } from "../event-bus";
import { scaleSprite } from "../utils/scale-sprite";
import { MAIN_COLOR } from "../config/colors";
import { POINT_LIGHT_BRIGHTNESS } from "../config/lights";
import { AppState } from "../app-state";
import { extractPointerCoords } from "../utils/extract-pointer-coords";

const LIGHT_COLOR_MAIN = 0xffffff;
const LIGHT_COLOR_ACCENT = MAIN_COLOR;
const AMBIENT_LIGHT_ANIM_DUR = 2.5;

@singleton()
export class BackgroundComponent {
  private shouldSyncLightWithDragger = false;
  private pointLight: PointLight;
  private ambientLight: AmbientLight;
  private container = new Container();
  private diffuse: Sprite;
  private normals: Sprite;
  private ambientTween: gsap.core.Tween;
  private pointTween: gsap.core.Tween;
  constructor(
    private pixiApp: PixiApp,
    private eventBus: EventBus,
    private appState: AppState
  ) {}
  mount() {
    const { pixiApp } = this;
    this.createBackground("monet", "monetNormal");
    this.createLights();
    this.container.addChild(
      this.normals,
      this.diffuse,
      this.pointLight,
      this.ambientLight
    );
    pixiApp.stage.addChild(
      new Layer(diffuseGroup),
      new Layer(normalGroup),
      new Layer(lightGroup),
      this.container
    );
    this.setupEvents();
  }
  private setupEvents() {
    this.eventBus.on("dragger/grab", () => {
      this.shouldSyncLightWithDragger = true;
      gsap.set(this.pointLight, {
        color: LIGHT_COLOR_ACCENT,
      });
    });
    this.eventBus.on("dragger/release", () => {
      this.shouldSyncLightWithDragger = false;
      gsap.set(this.pointLight, {
        color: LIGHT_COLOR_MAIN,
      });
    });
    this.eventBus.on("resize", () => {
      this.scaleBackground();
    });
    this.container.on("mouseup", this.onMouseUp);
  }
  fadeOut() {
    const duration = 1;
    this.disableAmbientLight();
    this.pointTween?.kill();
    return new Promise((res) => {
      this.pointTween = gsap.to(this.pointLight, {
        brightness: 0,
        duration,
        onComplete: res,
      });
    });
  }
  changeBackground(bg: string, normal: string) {
    this.diffuse.texture = this.pixiApp.loader.resources[bg].texture!;
    this.normals.texture = this.pixiApp.loader.resources[normal].texture!;
    this.scaleBackground();
  }
  toggleAmbientLight() {
    if (this.appState.isAmbient) {
      this.setPointLightBrightness(1);
      return this.disableAmbientLight();
    } else {
      return this.enableAmbientLight();
    }
  }
  enableAmbientLight() {
    this.appState.isAmbient = true;

    return new Promise((res) => {
      this.pointTween?.kill();
      this.ambientTween?.kill();
      this.pointTween = gsap.to(this.pointLight, {
        brightness: 0,
        ease: "Sine.easeIn",
        duration: 2.25,
      });
      this.ambientTween = gsap.to(this.ambientLight, {
        brightness: 1,
        ease: "Sine.easeIn",
        duration: AMBIENT_LIGHT_ANIM_DUR,
        onComplete: res,
      });
    });
  }
  disableAmbientLight() {
    this.appState.isAmbient = false;
    return new Promise((res) => {
      if (this.ambientLight.brightness > 0) {
        this.ambientTween?.kill();
        this.ambientTween = gsap.to(this.ambientLight, {
          brightness: 0,
          duration: 1,
          onComplete: res,
        });
      }
    });
  }
  setPointLightBrightness(brightness: number) {
    return new Promise((res) => {
      this.pointTween?.kill();
      this.pointTween = gsap.to(this.pointLight, {
        brightness,
        duration: 2,
        onComplete: res,
      });
    });
  }
  enableInteraction() {
    this.container.interactive = true;
    window.addEventListener("keydown", this.onKeyDown);
  }
  disableInteraction() {
    this.container.interactive = false;
    window.removeEventListener("keydown", this.onKeyDown);
  }
  private onMouseUp = () => {
    this.eventBus.emit("navBtn/day");
  };
  private onKeyDown = ({ key }: KeyboardEvent) => {
    if (key === "ArrowLeft") {
      this.eventBus.emit("navBtn/prev");
    } else if (key === "ArrowRight") {
      this.eventBus.emit("navBtn/next");
    } else if (key === "Escape") {
      this.eventBus.emit("navBtn/home");
    }
  };
  private createLights() {
    const light = new PointLight(LIGHT_COLOR_MAIN, 0);
    light.falloff = [0.75, 3, 10];
    const ambient = new AmbientLight(0xffffff, 0);
    this.pointLight = light;
    light.x = this.pixiApp.screen.width / 2;
    light.y = this.pixiApp.screen.height / 2;
    this.ambientLight = ambient;
  }
  private createBackground(bg: string, normal: string) {
    const { pixiApp } = this;
    const diffuse = new Sprite(pixiApp.loader.resources[bg].texture!);
    diffuse.parentGroup = diffuseGroup;
    const normals = Sprite.from(pixiApp.loader.resources[normal].texture!);
    normals.parentGroup = normalGroup;
    this.diffuse = diffuse;
    this.normals = normals;
    this.scaleBackground();
  }
  private scaleBackground() {
    const { diffuse, normals } = this;
    const { scale, pos } = scaleSprite(
      diffuse,
      document.documentElement.clientWidth,
      document.documentElement.clientHeight
    );
    diffuse.scale.set(scale, scale);
    diffuse.position.set(pos.x, pos.y);
    normals.position.set(pos.x, pos.y);
    normals.scale.set(scale, scale);
  }
  showInitialLight() {
    return new Promise((res) => {
      this.pointTween?.kill();
      this.pointTween = gsap.to(this.pointLight, {
        brightness: POINT_LIGHT_BRIGHTNESS,
        duration: 1,
        onComplete: res,
      });
    });
  }
  startInteraction() {
    window.addEventListener("mousemove", this.mouseMoveListener);
    window.addEventListener("touchmove", this.mouseMoveListener);
  }
  stopInteraction() {
    window.removeEventListener("mousemove", this.mouseMoveListener);
    window.removeEventListener("touchmove", this.mouseMoveListener);
  }
  private mouseMoveListener = (e: MouseEvent | TouchEvent) => {
    const bounds = this.pixiApp.view.getBoundingClientRect();

    const { x, y } = extractPointerCoords(e);
    const config = {
      x,
      y: y - bounds.top,
    };
    if (this.shouldSyncLightWithDragger) {
      gsap.to(this.pointLight, config);
    } else {
      gsap.to(this.pointLight, {
        ...config,
        duration: 1.25,
      });
    }
  };
}
